import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Input, 
  Table, 
  Button, 
  Dropdown, 
  Menu, 
  Typography, 
  Tabs, 
  Tag, 
  Card,
  Row,
  Col,
  Space,
  Avatar,
  Tooltip,
  Statistic,
  Badge,
  Select,
  DatePicker,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EllipsisOutlined, 
  DownOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  DollarOutlined,
  FilterOutlined,
  ReloadOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchJobs, deleteJob } from '../../api';
import './JobManagement.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const JobManagement = () => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch jobs every time the component mounts or when the location changes
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const jobsResponse = await fetchJobs();
        const jobsData = jobsResponse.data.map((job) => ({ ...job, key: job.id }));
        setDataSource(jobsData);
        filterData(jobsData, activeTabKey);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [location]);

  useEffect(() => {
    filterData(dataSource, activeTabKey);
  }, [dataSource, activeTabKey, searchTerm]);

  const filterData = (data, tabKey) => {
    let filteredJobs = [];
    
    // Filter by tab
    switch (tabKey) {
      case "2":
        filteredJobs = data.filter(job => job.status === 'Draft');
        break;
      case "3":
        filteredJobs = data.filter(job => job.status === 'Published');
        break;
      case "4":
        filteredJobs = data.filter(job => job.status === 'Filled');
        break;
      case "5":
        filteredJobs = data.filter(job => job.status === 'Open');
        break;
      case "6":
        filteredJobs = data.filter(job => job.status === 'Closed');
        break;
      default:
        filteredJobs = data;
    }

    // Filter by search term
    if (searchTerm) {
      filteredJobs = filteredJobs.filter(job => 
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredData(filteredJobs);
  };

  const handleEdit = (record) => {
    navigate('/employer/post-job', { state: { job: record } });
  };

  const handleDelete = async (key) => {
    try {
      await deleteJob(key);
      setDataSource(dataSource.filter((item) => item.key !== key));
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const handleViewDetails = (record) => {
    navigate(`/employer/job-details`, { state: { job: record } });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'default',
      'Published': 'blue',
      'Open': 'green',
      'Filled': 'purple',
      'Closed': 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusCount = (status) => {
    return dataSource.filter(job => job.status === status).length;
  };

  // Enhanced columns for table view
  const columns = [
    {
      title: 'Assignment',
      key: 'assignment',
      render: (record) => (
        <div>
          <div style={{ fontWeight: '600', color: '#006971', marginBottom: '4px' }}>
            {record.title}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.code} • {record.department}
          </Text>
        </div>
      ),
    },
    {
      title: 'Location & Schedule',
      key: 'location',
      render: (record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
            <EnvironmentOutlined style={{ marginRight: '4px', color: '#666' }} />
            <Text style={{ fontSize: '13px' }}>
              {record.address?.city}, {record.address?.state}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CalendarOutlined style={{ marginRight: '4px', color: '#666' }} />
            <Text style={{ fontSize: '12px', color: '#666' }}>
              {new Date(record.startDate).toLocaleDateString()} - {record.hoursPerDay}h/day
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Compensation',
      key: 'compensation',
      render: (record) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: '600', color: '#006971' }}>
            ${record.salary?.base || 0}/hr
          </div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Base Rate
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ fontWeight: '500' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetails(record)}
              style={{ color: '#006971' }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="text" 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record.key)}
              danger
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Card view component
  const JobCard = ({ job }) => (
    <Card
      size="small"
      style={{ 
        marginBottom: '12px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid #f0f0f0'
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <Title level={5} style={{ margin: 0, color: '#006971' }}>
              {job.title}
            </Title>
            <Tag color={getStatusColor(job.status)} style={{ marginLeft: '8px' }}>
              {job.status}
            </Tag>
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {job.code} • {job.department}
            </Text>
          </div>

          <Row gutter={[16, 8]}>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EnvironmentOutlined style={{ marginRight: '4px', color: '#666' }} />
                <Text style={{ fontSize: '12px' }}>
                  {job.address?.city}, {job.address?.state}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CalendarOutlined style={{ marginRight: '4px', color: '#666' }} />
                <Text style={{ fontSize: '12px' }}>
                  {new Date(job.startDate).toLocaleDateString()}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <DollarOutlined style={{ marginRight: '4px', color: '#666' }} />
                <Text style={{ fontSize: '12px', fontWeight: '500' }}>
                  ${job.salary?.base || 0}/hr
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TeamOutlined style={{ marginRight: '4px', color: '#666' }} />
                <Text style={{ fontSize: '12px' }}>
                  {job.numberOfOpenings} position{job.numberOfOpenings > 1 ? 's' : ''}
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        <div style={{ marginLeft: '16px' }}>
          <Space direction="vertical" size="small">
            <Button 
              type="primary" 
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(job)}
              style={{ 
                background: 'linear-gradient(45deg, #006971, #00a0a0)',
                border: 'none',
                width: '100px'
              }}
            >
              View
            </Button>
            <Button 
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(job)}
            >
              Edit
            </Button>
          </Space>
        </div>
      </div>
    </Card>
  );

  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  const handleExport = () => {
    try {
      // Prepare data for export
      const exportData = filteredData.map(job => ({
        'Assignment Code': job.code,
        'Title': job.title,
        'Department': job.department,
        'Status': job.status,
        'Location': `${job.address?.city}, ${job.address?.state}`,
        'Start Date': new Date(job.startDate).toLocaleDateString(),
        'End Date': new Date(job.endDate).toLocaleDateString(),
        'Hours Per Day': job.hoursPerDay,
        'Days Per Week': job.daysPerWeek,
        'Base Salary': `$${job.salary?.base || 0}/hr`,
        'Overtime Salary': `$${job.salary?.overtime || 0}/hr`,
        'Manager': job.managerName,
        'Number of Positions': job.numberOfOpenings,
        'Unit': job.unit,
        'Type': job.type,
        'Required Skills': job.requiredSkills?.join(', ') || '',
        'Preferred Skills': job.preferredSkills?.join(', ') || '',
        'Languages': job.languages?.join(', ') || '',
        'Degree Required': job.degree,
        'Float Position': job.isFloat ? 'Yes' : 'No',
        'Shift': job.shift,
        'Street Address': job.address?.street,
        'ZIP Code': job.address?.zipCode,
        'Schedule Notes': job.scheduleNotes || '',
        'Job Description': job.duties || ''
      }));

      // Convert to CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            // Escape commas and quotes in CSV
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date and filter info
      const currentDate = new Date().toISOString().split('T')[0];
      const statusFilter = activeTabKey === '1' ? 'All' : 
                          activeTabKey === '2' ? 'Draft' :
                          activeTabKey === '3' ? 'Published' :
                          activeTabKey === '4' ? 'Filled' :
                          activeTabKey === '5' ? 'Open' : 'Closed';
      const filename = `assignments_${statusFilter}_${currentDate}.csv`;
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      console.log(`Exported ${exportData.length} assignments to ${filename}`);
      
    } catch (error) {
      console.error('Export failed:', error);
      // You could add a notification here
    }
  };

  return (
    <Content style={{  background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <div style={{ 
        background: 'white', 
        padding: '24px', 
        borderRadius: '12px', 
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#006971' }}>
              Assignment Management
            </Title>
            <Text type="secondary">
              Manage and track all your healthcare assignments
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              <Button 
                icon={<ReloadOutlined />}
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
                             <Button 
                 icon={<ExportOutlined />}
                 onClick={handleExport}
                 disabled={filteredData.length === 0}
               >
                 Export ({filteredData.length})
               </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                onClick={() => navigate('/employer/post-job')}
                style={{ 
                  background: 'linear-gradient(45deg, #006971, #00a0a0)',
                  border: 'none',
                  height: '40px',
                  paddingLeft: '24px',
                  paddingRight: '24px'
                }}
              >
                Create New Assignment
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Assignments"
                value={dataSource.length}
                valueStyle={{ color: '#006971', fontSize: '24px', fontWeight: '600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Open Positions"
                value={getStatusCount('Open')}
                valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: '600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Published"
                value={getStatusCount('Published')}
                valueStyle={{ color: '#1890ff', fontSize: '24px', fontWeight: '600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Filled"
                value={getStatusCount('Filled')}
                valueStyle={{ color: '#722ed1', fontSize: '24px', fontWeight: '600' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Filters and Controls */}
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search assignments, codes, locations..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '6px' }}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Department"
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="ICU">ICU</Option>
              <Option value="Emergency">Emergency</Option>
              <Option value="Surgery">Surgery</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Location"
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="New York">New York</Option>
              <Option value="California">California</Option>
              <Option value="Texas">Texas</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Space>
              <Tooltip title="Table View">
                <Button 
                  type={viewMode === 'table' ? 'primary' : 'default'}
                  icon={<FilterOutlined />}
                  onClick={() => setViewMode('table')}
                />
              </Tooltip>
              <Tooltip title="Card View">
                <Button 
                  type={viewMode === 'cards' ? 'primary' : 'default'}
                  icon={<TeamOutlined />}
                  onClick={() => setViewMode('cards')}
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Content */}
      <Card style={{ borderRadius: '8px' }}>
                 <Tabs 
           activeKey={activeTabKey} 
           onChange={handleTabChange}
           type="card"
           style={{ marginBottom: '16px' }}
         >
           <TabPane 
             tab={
               <span>
                 All <Tag color="blue" style={{ marginLeft: '4px' }}>{dataSource.length}</Tag>
               </span>
             } 
             key="1"
           />
           <TabPane 
             tab={
               <span>
                 Draft <Tag color="default" style={{ marginLeft: '4px' }}>{getStatusCount('Draft')}</Tag>
               </span>
             } 
             key="2"
           />
           <TabPane 
             tab={
               <span>
                 Published <Tag color="blue" style={{ marginLeft: '4px' }}>{getStatusCount('Published')}</Tag>
               </span>
             } 
             key="3"
           />
           <TabPane 
             tab={
               <span>
                 Filled <Tag color="purple" style={{ marginLeft: '4px' }}>{getStatusCount('Filled')}</Tag>
               </span>
             } 
             key="4"
           />
           <TabPane 
             tab={
               <span>
                 Open <Tag color="green" style={{ marginLeft: '4px' }}>{getStatusCount('Open')}</Tag>
               </span>
             } 
             key="5"
           />
           <TabPane 
             tab={
               <span>
                 Closed <Tag color="red" style={{ marginLeft: '4px' }}>{getStatusCount('Closed')}</Tag>
               </span>
             } 
             key="6"
           />
         </Tabs>

        {viewMode === 'table' ? (
          <Table 
            dataSource={filteredData} 
            columns={columns} 
            loading={loading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} assignments`
            }}
            scroll={{ x: 800 }}
            style={{ borderRadius: '8px' }}
          />
        ) : (
          <div>
            {filteredData.length > 0 ? (
              <Row gutter={[16, 16]}>
                {filteredData.map(job => (
                  <Col xs={24} lg={12} xl={8} key={job.key}>
                    <JobCard job={job} />
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{ textAlign: 'center', padding: '48px' }}>
                <Text type="secondary">No assignments found</Text>
              </div>
            )}
          </div>
        )}
      </Card>
    </Content>
  );
};

export default JobManagement;
